import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Form, FormikProvider, useFormik } from 'formik';
import {
  Box,
  Card,
  Grid,
  Stack,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  IconButton,
} from '@material-ui/core';
import { LoadingButton } from '@material-ui/lab';
import Autocomplete from '@material-ui/lab/Autocomplete';
import fakeRequest from 'src/utils/fakeRequest';
import useAuth from 'src/hooks/useAuth';
import Icon from '@iconify/react';
import eyeFill from '@iconify/icons-eva/eye-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
import { REST_API } from 'src/constants/DefaultValues';
import { GOOGLE_MAPS_API_KEY } from 'src/DefaultValues';
import axios from 'axios';

const loadGoogleMapsScript = (apiKey) => {
  return new Promise((resolve, reject) => {
    if (window.google && window.google.maps && window.google.maps.places) {
      resolve(window.google);
      return;
    }
    const existingScript = document.getElementById('google-maps-places-script');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(window.google));
      existingScript.addEventListener('error', (e) => reject(e));
      return;
    }
    const script = document.createElement('script');
    script.id = 'google-maps-places-script';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google);
    script.onerror = (e) => reject(e);
    document.head.appendChild(script);
  });
};

const parsePlaceDetails = (placeResult, fallbackName) => {
  const components = placeResult?.address_components || [];
  let city = '';
  let state = '';
  let postalCode = '';
  let route = '';
  let sublocality = '';

  components.forEach((c) => {
    const types = c.types || [];
    if (types.some((t) => t.includes('postal_code'))) {
      postalCode = c.long_name || c.short_name;
    } else if (types.includes('administrative_area_level_1')) {
      state = c.long_name;
    } else if (types.includes('locality')) {
      city = c.long_name;
    } else if (!city && (types.includes('administrative_area_level_2') || types.includes('postal_town'))) {
      city = c.long_name;
    } else if (!city && (types.includes('sublocality_level_1') || types.includes('sublocality'))) {
      city = c.long_name;
    }

    if (types.includes('route')) {
      route = c.long_name;
    }
    if (types.includes('sublocality_level_1') || types.includes('sublocality') || types.includes('neighborhood')) {
      sublocality = c.long_name;
    }
  });

  if (!postalCode) {
    const textToSearch = [
      placeResult?.formatted_address,
      fallbackName
    ].filter(Boolean).join(' ');
    const pinMatch = textToSearch.match(/\b([1-9][0-9]{5})\b/);
    if (pinMatch) {
      postalCode = pinMatch[1];
    }
  }

  const lat = placeResult?.geometry?.location?.lat
    ? (typeof placeResult.geometry.location.lat === 'function' ? placeResult.geometry.location.lat() : placeResult.geometry.location.lat)
    : null;
  const lng = placeResult?.geometry?.location?.lng
    ? (typeof placeResult.geometry.location.lng === 'function' ? placeResult.geometry.location.lng() : placeResult.geometry.location.lng)
    : null;

  return {
    landmark: fallbackName || placeResult?.name || placeResult?.formatted_address || '',
    address: [route, sublocality].filter(Boolean).join(', ') || placeResult?.formatted_address || '',
    city,
    state,
    postalCode: postalCode ? String(postalCode).replace(/\D/g, '').slice(0, 6) : '',
    latitude: lat,
    longitude: lng,
  };
};
import {
  isSelfRegUnsetProfile,
} from 'src/utils/customerDisplay';
import { indianMobileSchema } from 'src/utils/authIdentifier';

MayuraCustomerAddEditForm.propTypes = {
  isEdit: PropTypes.bool,
  currentUser: PropTypes.object,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
  submitLabel: PropTypes.string,
};

export default function MayuraCustomerAddEditForm({ isEdit, currentUser, onClose, onSuccess, submitLabel }) {
  const { enqueueSnackbar } = useSnackbar();
  const [showPassword, setShowPassword] = useState(false);
  const [branchList, setBranchList] = useState([]);
  const { user } = useAuth()
  const [loading, setLoading] = useState(false);
  const customerTypeOptions = [
  { label: 'Consignor', value: 1 },
  { label: 'Consignee', value: 2 },
];
  const taxTypeOptions = ['GST', 'URP'];

  const getCustomerTypeValue = (customer) => {
    if (isSelfRegUnsetProfile(customer)) {
      return null;
    }
    return [1, 2].includes(Number(customer?.customer_type))
      ? customerTypeOptions.find((type) => type.value === Number(customer.customer_type))
      : null;
  };

  const getBranchValue = (customer) => {
    if (!customer?.branch_id || isSelfRegUnsetProfile(customer)) {
      return null;
    }
    return branchList.find((branch) => branch.id === customer.branch_id) || null;
  };

  // DB column is land_line; accept aliases so edit never drops a present value
  const resolveLandline = (customer) => {
    const raw =
      customer?.land_line ??
      customer?.landline ??
      customer?.landline_no ??
      '';
    if (raw == null) return '';
    return String(raw);
  };

  const fetchBranches = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${REST_API}branches/get-all-active-branches`);
      setBranchList(res.data.status === 1 ? res.data.data : []);
    } catch (error) {
      console.error('Error fetching branches:', error);
      setBranchList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  const [landmarkOptions, setLandmarkOptions] = useState([]);
  const [landmarkInputValue, setLandmarkInputValue] = useState(currentUser?.landmark || '');

  useEffect(() => {
    const apiKey = GOOGLE_MAPS_API_KEY || 'AIzaSyD1VmydeASm4dawh3VO8b-lYlvM5exe4NY';
    loadGoogleMapsScript(apiKey).catch((err) => console.error('Error loading Google Maps script:', err));
  }, []);

  useEffect(() => {
    if (currentUser?.landmark) {
      setLandmarkInputValue(currentUser.landmark);
    }
  }, [currentUser?.landmark]);

  useEffect(() => {
    if (!landmarkInputValue || landmarkInputValue.trim().length < 2) {
      setLandmarkOptions([]);
      return;
    }

    if (!window.google || !window.google.maps || !window.google.maps.places) {
      return;
    }

    const autocompleteService = new window.google.maps.places.AutocompleteService();
    autocompleteService.getPlacePredictions(
      {
        input: landmarkInputValue,
        componentRestrictions: { country: 'in' },
      },
      (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setLandmarkOptions(predictions);
        } else {
          setLandmarkOptions([]);
        }
      }
    );
  }, [landmarkInputValue]);

  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };

  const CustomerSchema = Yup.object().shape({
    name: Yup.string()
      .matches(/^[a-zA-Z0-9]+(?: [a-zA-Z0-9]+)*$/, 'Only alphanumeric characters and single spaces allowed')
      .required('Name is required'),

    taxType: Yup.string()
      .nullable()
      .required('Tax Type is required')
      .oneOf(['GST', 'URP'], 'Invalid tax type'),

    gst: Yup.string()
      .when('taxType', {
        is: 'GST',
        then: Yup.string()
          .matches(/^[A-Z0-9]{1,15}$/, 'Only uppercase letters and numbers allowed, max 15 chars')
          .required('GST No is required'),
        otherwise: Yup.string().nullable(),
      }),

    urp: Yup.string()
      .when('taxType', {
        is: 'URP',
        then: Yup.string()
          .matches(/^[A-Z0-9]{1,15}$/, 'Only uppercase letters and numbers allowed, max 15 chars')
          .required('URP No is required'),
        otherwise: Yup.string().nullable(),
      }),

    mobile: indianMobileSchema('Mobile No is required'),

    branchLocation: Yup.object()
      .nullable()
      .required('Branch is required'),

    customer_type: Yup.object()
      .nullable()
      .required('Customer Type is required'),

    city: Yup.string()
      .trim()
      .required('City is required'),

    landline: Yup.string()
      .nullable()
      .notRequired()
      .matches(/^[0-9]{6,12}$/, {
        message: 'Only digits, min 6 and max 12 digits',
        excludeEmptyString: true,
      }),

    email: Yup.string()
      .email('Invalid email format')
      .required('Email is required')
      .nullable(false),

    password: isEdit
      ? Yup.string()
          .transform((value) => (value === '' ? undefined : value))
          .notRequired()
          .min(8, 'Password must be at least 8 characters')
          .max(30, 'Password must not exceed 30 characters')
          .matches(
            /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])/,
            {
              message: 'Must include uppercase, lowercase, number, and special character',
              excludeEmptyString: true,
            }
          )
      : Yup.string()
          .min(8, 'Password must be at least 8 characters')
          .max(30, 'Password must not exceed 30 characters')
          .matches(
            /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])/,
            'Must include uppercase, lowercase, number, and special character'
          )
          .required('Password is required'),

    address: Yup.string()
      .trim()
      .required('Address is required'),

    state: Yup.string()
      .trim()
      .required('State is required'),

    landmark: Yup.string()
      .trim()
      .required('Landmark is required'),

    postalCode: Yup.string()
      .matches(/^[0-9]{5,10}$/, 'Only digits between 5 to 10 length')
      .nullable(),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      customer_id: currentUser?.customer_id || '',
      customer_type: getCustomerTypeValue(currentUser),
      name: currentUser?.user_name || '',
      mobile: currentUser?.phone_number || '',
      taxType: currentUser?.urp ? 'URP' : currentUser?.gst ? 'GST' : null,
      gst: currentUser?.gst || '',
      urp: currentUser?.urp || '',
      city: currentUser?.city === '-' ? '' : currentUser?.city || '',
      branchLocation: getBranchValue(currentUser),
      address: currentUser?.address === '-' ? '' : currentUser?.address || '',
      state: currentUser?.state || '',
      landmark: currentUser?.landmark || '',
      postalCode: currentUser?.postal_Code || '',
      landline: resolveLandline(currentUser),
      email: currentUser?.email || '',
      password: currentUser?.password_view || '',
      isSelf: isSelfRegUnsetProfile(currentUser)
        ? false
        : currentUser?.is_self === 1,
      latitude: currentUser?.latitude || null,
      longitude: currentUser?.longitude || null,
    },

    validationSchema: CustomerSchema,
    onSubmit: async (values, { setSubmitting, resetForm, setErrors }) => {
      try {
        await fakeRequest(500)

        const payload = {
          ...values,
          addedBy: user?.id,
          customer_type: values.customer_type?.value || null,
        }

        if (isEdit && !(values.password && String(values.password).trim())) {
          delete payload.password
        }

        if (isEdit && currentUser?.user_id) {
          payload.user_id = currentUser.user_id
          payload.customer_id = values.customer_id
          payload.updated_by = user?.id
        }

        const apiEndpoint = isEdit
          ? `${REST_API}customers/update-customer`
          : `${REST_API}customers/add-customer`

        const res = await axios.post(apiEndpoint, payload)
        if (res.data.status === 1) {
          enqueueSnackbar(
            isEdit
              ? 'Customer updated successfully'
              : 'Customer created successfully',
            { variant: 'success' },
          )
          resetForm()
          setSubmitting(false)
          if (onSuccess) {
            onSuccess(res.data.data)
          }
          onClose()
        } else if (res.data.status === 2) {
          enqueueSnackbar(
            res.data.message || 'No changes were made to the customer profile',
            { variant: 'info' },
          )
          setSubmitting(false)
          onClose()
        } else {
          const message = res.data.message || 'Operation failed'
          enqueueSnackbar(
            /phone number already exist/i.test(message)
              ? 'Phone number already exist'
              : message,
            { variant: 'error' },
          )
          setSubmitting(false)
        }
      } catch (error) {
        console.error(error)
        setSubmitting(false)
        const message =
          error.response?.data?.message || error.message || 'Operation failed'
        enqueueSnackbar(
          /phone number already exist/i.test(message)
            ? 'Phone number already exist'
            : message,
          { variant: 'error' },
        )
        setErrors(error)
      }
    },
  })

  const {
    values,
    errors,
    touched,
    handleSubmit,
    getFieldProps,
    setFieldValue,
    setFieldTouched,
    isSubmitting
  } = formik;

  // Re-apply landline when edit payload arrives / branches reload (enableReinitialize)
  useEffect(() => {
    if (!isEdit || !currentUser) return;
    const next = resolveLandline(currentUser);
    if (values.landline !== next) {
      setFieldValue('landline', next, false);
    }
  }, [
    isEdit,
    currentUser?.customer_id,
    currentUser?.id,
    currentUser?.land_line,
    currentUser?.landline,
    currentUser?.landline_no,
  ]);

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <Card sx={{ p: 3, boxShadow: 'none' }}>
          <Stack container gap={2} direction="column">
            <Box sx={{ pl: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.isSelf}
                    onChange={(e) => setFieldValue('isSelf', e.target.checked)}
                  />
                }
                label="Self"
              />
            </Box>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignContent={'center'}
            >
              <TextField
                fullWidth
                label="Name"
                {...getFieldProps('name')}
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name && errors.name}
              />
              <TextField
                fullWidth
                label="Mobile No"
                {...getFieldProps('mobile')}
                error={touched.mobile && Boolean(errors.mobile)}
                helperText={touched.mobile && errors.mobile}
              />
              <TextField
                fullWidth
                label="Landline No"
                autoComplete="off"
                {...getFieldProps('landline')}
                error={touched.landline && Boolean(errors.landline)}
                helperText={touched.landline && errors.landline}
              />
            </Stack>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignContent={'center'}
            >
              <TextField
                fullWidth
                label="Email"
                {...getFieldProps('email')}
                error={touched.email && Boolean(errors.email)}
                helperText={touched.email && errors.email}
              />
              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                {...getFieldProps('password')}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleShowPassword} edge="end">
                        <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                error={touched.password && Boolean(errors.password)}
                helperText={touched.password && errors.password}
              />
              <Autocomplete
                fullWidth
                options={taxTypeOptions}
                value={values.taxType}
                onChange={(_, newValue) => {
                  setFieldValue('taxType', newValue);
                  if (newValue === 'GST') {
                    setFieldValue('urp', '');
                  } else if (newValue === 'URP') {
                    setFieldValue('gst', '');
                  } else {
                    setFieldValue('gst', '');
                    setFieldValue('urp', '');
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tax Type"
                    error={touched.taxType && Boolean(errors.taxType)}
                    helperText={touched.taxType && errors.taxType}
                  />
                )}
              />
            </Stack>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignContent={'center'}
            >
              {values.taxType === 'GST' && (
                <TextField
                  fullWidth
                  label="GST No"
                  {...getFieldProps('gst')}
                  error={touched.gst && Boolean(errors.gst)}
                  helperText={touched.gst && errors.gst}
                />
              )}
              {values.taxType === 'URP' && (
                <TextField
                  fullWidth
                  label="URP No"
                  {...getFieldProps('urp')}
                  error={touched.urp && Boolean(errors.urp)}
                  helperText={touched.urp && errors.urp}
                />
              )}
            </Stack>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignContent={'center'}
            >
              <Autocomplete
                options={branchList}
                fullWidth
                loading={loading}
                getOptionLabel={(option) =>
                  `${option.branch_address} - ${option.branch_code}`
                }
                value={values.branchLocation}
                onChange={(_, newValue) =>
                  setFieldValue('branchLocation', newValue)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Branch Added"
                    fullWidth
                    error={
                      touched.branchLocation && Boolean(errors.branchLocation)
                    }
                    helperText={touched.branchLocation && errors.branchLocation}
                  />
                )}
              />
              <TextField
                fullWidth
                label="Address"
                {...getFieldProps('address')}
                error={touched.address && Boolean(errors.address)}
                helperText={touched.address && errors.address}
              />

              <Autocomplete
                freeSolo
                fullWidth
                options={landmarkOptions}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return option.description || '';
                }}
                inputValue={landmarkInputValue}
                onInputChange={(event, newInputValue) => {
                  setLandmarkInputValue(newInputValue);
                  setFieldValue('landmark', newInputValue);
                }}
                onChange={(event, selectedValue) => {
                  if (!selectedValue) return;
                  if (typeof selectedValue === 'object' && selectedValue.place_id) {
                    const mainText = selectedValue.structured_formatting?.main_text || selectedValue.description;
                    setFieldValue('landmark', mainText);
                    setLandmarkInputValue(mainText);
                    setFieldTouched('landmark', true, false);

                    if (window.google && window.google.maps) {
                      const geocoder = new window.google.maps.Geocoder();
                      geocoder.geocode({ placeId: selectedValue.place_id }, (results, status) => {
                        if (status === 'OK' && results && results.length > 0) {
                          const placeDesc = selectedValue.description || selectedValue.structured_formatting?.secondary_text || mainText;
                          let parsed = parsePlaceDetails(results[0], placeDesc);

                          if (!parsed.postalCode) {
                            for (const res of results) {
                              const temp = parsePlaceDetails(res, placeDesc);
                              if (temp.postalCode) {
                                parsed.postalCode = temp.postalCode;
                                break;
                              }
                            }
                          }

                          if (!parsed.postalCode && selectedValue) {
                            const descText = selectedValue.description || selectedValue.structured_formatting?.secondary_text || '';
                            const match = descText.match(/\b([1-9][0-9]{5})\b/);
                            if (match) {
                              parsed.postalCode = match[1];
                            }
                          }

                          if (parsed.landmark) {
                            setFieldValue('landmark', parsed.landmark);
                            setLandmarkInputValue(parsed.landmark);
                          }
                          if (parsed.city) setFieldValue('city', parsed.city);
                          if (parsed.state) setFieldValue('state', parsed.state);
                          if (parsed.postalCode) setFieldValue('postalCode', parsed.postalCode);
                          if (parsed.latitude && parsed.longitude) {
                            setFieldValue('latitude', parsed.latitude);
                            setFieldValue('longitude', parsed.longitude);
                          }
                          if (parsed.address && !values.address) {
                            setFieldValue('address', parsed.address);
                          }

                          // Fallback reverse-geocoding lookup if postalCode is not in the top-level place object
                          if (!parsed.postalCode && parsed.latitude && parsed.longitude) {
                            geocoder.geocode(
                              { location: { lat: parsed.latitude, lng: parsed.longitude } },
                              (revResults, revStatus) => {
                                let foundPostalCode = '';
                                if (revStatus === 'OK' && revResults && revResults.length > 0) {
                                  for (const res of revResults) {
                                    const revParsed = parsePlaceDetails(res);
                                    if (revParsed.postalCode) {
                                      foundPostalCode = revParsed.postalCode;
                                      break;
                                    }
                                  }
                                }
                                if (foundPostalCode) {
                                  setFieldValue('postalCode', foundPostalCode);
                                } else {
                                  fetch(`https://nominatim.openstreetmap.org/reverse?lat=${parsed.latitude}&lon=${parsed.longitude}&format=json`)
                                    .then((r) => r.json())
                                    .then((data) => {
                                      const pCode = data?.address?.postcode;
                                      if (pCode) {
                                        const cleanCode = String(pCode).replace(/\D/g, '').slice(0, 6);
                                        if (cleanCode) {
                                          setFieldValue('postalCode', cleanCode);
                                        }
                                      }
                                    })
                                    .catch((err) => console.error('Postal code reverse fetch error:', err));
                                }
                              }
                            );
                          }
                        }
                      });
                    }
                  } else if (typeof selectedValue === 'string') {
                    setFieldValue('landmark', selectedValue);
                    setLandmarkInputValue(selectedValue);
                    setFieldTouched('landmark', true, false);
                  }
                }}
                value={values.landmark || ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label="Landmark (Search from Google Maps)"
                    error={touched.landmark && Boolean(errors.landmark)}
                    helperText={touched.landmark && errors.landmark}
                  />
                )}
              />
            </Stack>

            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignContent={'center'}
            >
              <TextField
                fullWidth
                label="City"
                {...getFieldProps('city')}
                InputProps={{ readOnly: true }}
                error={touched.city && Boolean(errors.city)}
                helperText={touched.city && errors.city}
              />
              <TextField
                fullWidth
                label="State"
                {...getFieldProps('state')}
                InputProps={{ readOnly: true }}
                error={touched.state && Boolean(errors.state)}
                helperText={touched.state && errors.state}
              />

              <TextField
                fullWidth
                label="Postal Code"
                {...getFieldProps('postalCode')}
                InputProps={{ readOnly: true }}
                error={touched.postalCode && Boolean(errors.postalCode)}
                helperText={touched.postalCode && errors.postalCode}
              />
              <Autocomplete
                fullWidth
                options={customerTypeOptions}
                getOptionLabel={(option) => option.label}
                value={values.customer_type}
                onChange={(_, newValue) => {
                  setFieldValue('customer_type', newValue);
                  setFieldTouched('customer_type', true, false);
                }}
                onBlur={() => setFieldTouched('customer_type', true)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="Customer Type"
                    error={touched.customer_type && Boolean(errors.customer_type)}
                    helperText={touched.customer_type && errors.customer_type}
                  />
                )}
              />
            </Stack>
            
            <Grid item xs={12}>
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <LoadingButton
                  sx={{ boxShadow: 'none' }}
                  startIcon={
                    <Stack
                      className={
                        isEdit ? 'ic--twotone-edit' : 'lets-icons--add-duotone'
                      }
                      sx={{ width: 24, height: 24 }}
                    />
                  }
                  type="submit"
                  variant="contained"
                  loading={isSubmitting}
                >
                  {submitLabel || (isEdit ? 'Update Customer' : 'Add Customer')}
                </LoadingButton>
              </Stack>
            </Grid>
          </Stack>
        </Card>
      </Form>
    </FormikProvider>
  )
}
